import { Injectable, effect, untracked, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { debounce, get, isEmpty, once, sortBy, uniqueId } from 'lodash';
import { IqEventBusQueueService } from './iq-event-bus-queue.service';
import {
    EventInfo,
    IqEventBrokerQueueState,
    IqEventBusBrokerHandler,
    IqEventBusBrokerHandlerOptions,
    IqEventBusNode,
    ServiceConsumerEventName,
    CustomElementConsumerEventName,
    EventQuery,
} from './iq-event-bus.models';
/**
 * The IqEventBusBrokerService is a service that allows you to register event handlers that listen for specific events and run a callback when the event is dispatched.
 * This service will be leveraged for all components and we can have it communicate with each other in a decentralize way.
 */
@Injectable({
    providedIn: 'root',
})
export class IqEventBusBrokerService extends ComponentStore<IqEventBrokerQueueState> {
    private _queue: IqEventBusQueueService = new IqEventBusQueueService();

    get queue() {
        return this._queue;
    }

    getEvents(eventQuery: EventQuery): Signal<EventInfo[]> {
        return this._queue.filteredEvents(eventQuery);
    }
    getEventNodes(eventQuery: EventQuery): Signal<IqEventBusNode[]> {
        return this._queue.filteredEventNodes(eventQuery);
    }

    constructor() {
        super({ handlers: [] });

        window.addEventListener(
            ServiceConsumerEventName,
            (event: CustomEvent) => {
                this.dispatch(event.detail);
            },
        );

        effect(() => {
            /**
             * Converts all unhandled events into a map where the key is the topic and the value is an array of nodes
             */
            const unhandledEventMap: Map<string, IqEventBusNode[]> =
                this.queue.unhandledEventNodeMap();

            /**
             * Checks to see if there are any unhandled events
             */
            const run: boolean = !this.queue.isAllHandled();

            /**
             * If there are no unhandled events, then we can return early
             */
            if (!run) return;

            /**
             * Gets all the topics from the map that haven't been handled
             */
            const topics: string[] = Array.from(unhandledEventMap.keys());

            /**
             * Iterates over each topic and checks if there are any handlers that match the topic
             */
            topics.forEach((topic) => {
                const nodes: IqEventBusNode[] = unhandledEventMap.get(topic);

                /**
                 * For each node, it will run the event and payload that matches each handler
                 */
                nodes.forEach((node: IqEventBusNode) => {
                    const handlers = this.state().handlers.filter(
                        (handler: IqEventBusBrokerHandler) =>
                            handler.name === node.topic && !handler.removed,
                    );
                    const currentEvent: EventInfo = {
                        ...node.evt,
                        handlers: [],
                    };

                    

                    //""

                    /**
                     * Updates the state to show that the event has been handled
                     */
                    untracked(() => {
                        /**
                         * Calls the callback for each handler that matches the topic
                         */
                        handlers.forEach((handler) => {
                            handler.callback(currentEvent.payload ?? null);
                            currentEvent.handlers = [
                                ...currentEvent.handlers,
                                {
                                    ...handler.opts,
                                    handlerId: handler.id,
                                },
                            ];
                        });
                        this.handleEvent({ ...currentEvent }); // This will update the handler's lastFired so we know when the last time it was fired was
                        this.queue.handleEvent({ ...node, evt: currentEvent }); // Indicates that the event has been handled and when it fired

                        Object.values(this._handledEventHandlers).forEach(
                            (handler) => {
                                handler({ ...node, evt: currentEvent });
                            },
                        );

                        /**
                         * Dispatches the event to Web Components/Custom Components so that components
                         * not tied to Angular can listen to the event.
                         */
                        window.dispatchEvent(
                            new CustomEvent<EventInfo>(
                                CustomElementConsumerEventName,
                                {
                                    detail: currentEvent,
                                },
                            ),
                        );
                    });
                });
            });
        });
    }

    private _handledEventHandlers: Record<
        string,
        (event: IqEventBusNode) => void
    > = {};

    watchHandledEvents(callback: (event: IqEventBusNode) => void): () => void {
        const id = uniqueId('event-handled-watcher-');

        this._handledEventHandlers[id] = callback;

        return () => {
            delete this._handledEventHandlers[id];
        };
    }

    readonly handleEvent = this.updater(
        (state: IqEventBrokerQueueState, evt: EventInfo) => {
            return {
                handlers: state.handlers.map(
                    (
                        handler: IqEventBusBrokerHandler,
                    ): IqEventBusBrokerHandler => {
                        return {
                            ...handler,
                            lastFired:
                                handler.name === evt.name
                                    ? Date.now()
                                    : handler.lastFired,
                        };
                    },
                ),
            };
        },
    );

    /**
     * Adds a new event handler to the queue
     *
     * @param handler - The handler to add
     */
    readonly addHandler = this.updater(
        (state: IqEventBrokerQueueState, handler: IqEventBusBrokerHandler) => {
            return {
                handlers: [...state.handlers, handler],
            };
        },
    );
    readonly removeHandler = this.updater(
        (state: IqEventBrokerQueueState, id: string) => {
            return {
                handlers: state.handlers.map(
                    (
                        handler: IqEventBusBrokerHandler,
                    ): IqEventBusBrokerHandler => ({
                        ...handler,
                        removed: handler.id === id ? true : !!handler.removed,
                        removedInMs:
                            handler.id === id && !handler.removed
                                ? Date.now()
                                : handler.removedInMs,
                    }),
                ),
            };
        },
    );

    private _isInitialized = false;

    initialize() {
        if (this._isInitialized) return;

        this._isInitialized = true;
        this._queue.start();
        this.setState({ handlers: [] });
    }

    dispatch(evt: EventInfo) {
        this._queue.addEvent(evt);
    }

    dispatchAll(events: EventInfo[]) {
        this._queue.addEvents(events);
    }

    /**
     * Registers a new event handler that listens for a specific event and runs a callback when the event is dispatched
     *
     * @param name name of the event to listen for
     * @param callback the call back to run when this event is dispatched
     * @param opts options for the handler
     * @param opts.immediate if the handler should be called immediately. Defaults to true
     * @returns a function to remove the handler
     */
    on(
        name: string,
        callback: (payload?: any) => void,
        opts: IqEventBusBrokerHandlerOptions = { immediate: true },
    ): IqEventBusBrokerHandler {
        const handler: IqEventBusBrokerHandler = this._setUpHandler(
            name,
            callback,
            opts,
        );

        return handler;
    }

    /**
     * For ease of use, you can register multiple handlers at once by passing in a map of event names and their respective callbacks and options.
     * This is useful when you want to register multiple handlers at once and not have to keep track
     * @param map - Record of event names and their respective callbacks and options.
     * @returns a record of the handlers that were registered
     */
    registerMap(
        map: Record<
            string,
            {
                callback: (payload?: any) => void;
                opts?: IqEventBusBrokerHandlerOptions;
            }
        >,
    ): Record<string, IqEventBusBrokerHandler> {
        return Object.keys(map).reduce(
            (
                currentRegisters: Record<string, IqEventBusBrokerHandler>,
                eventName: string,
            ) => {
                const callback = map[eventName].callback;
                const opts = map[eventName].opts ?? { immediate: true };

                return {
                    ...currentRegisters,
                    [eventName]: opts.once
                        ? this.once(eventName, callback, opts)
                        : this.on(eventName, callback, opts),
                };
            },
            {},
        );
    }

    unregisterMap(map: Record<string, IqEventBusBrokerHandler>): void {
        Object.keys(map ?? {}).forEach((eventName: string) => {
            this.off(map[eventName]);
        }, {});
    }

    /**
     * Sets the handler to remove
     *
     * The reason to set to remove rather than delete is so that we can use it to debug
     * or log it. This way if we have weird behavior we can debug it.
     *
     * @param handler - The handler to remove
     */
    off(handler: IqEventBusBrokerHandler): void {
        this.removeHandler(handler.id);
    }

    /**
     * Adds a new event handler that listens for a specific event and runs a callback when the event is dispatched. The handler will only be called once
     * which means that if more events of the same name tries to dispatch, the handler will not be called again.
     *
     * This is good for things that only need to be invoked once per component lifecycle.
     *
     * @param name - Name of the event to listen for
     * @param callback - The call back to run when this event is dispatched
     * @param opts - Options for the handler
     * @returns = An instance of the handler
     */
    once(
        name: string,
        callback: (payload?: any) => void,
        opts: IqEventBusBrokerHandlerOptions = { immediate: false },
    ): IqEventBusBrokerHandler {
        const handler: IqEventBusBrokerHandler = this._setUpHandler(
            name,
            once(callback), // This will only run the callback once
            {
                ...opts,
                once: true, // Indicates that this handler was only called once
            },
        );

        return handler;
    }

    private _setUpHandler(
        name: string,
        callback: (payload?: any) => void,
        opts: IqEventBusBrokerHandlerOptions,
    ): IqEventBusBrokerHandler {
        const handler: IqEventBusBrokerHandler = {
            name,
            callback,
            opts,
            id: uniqueId('event-handler-'),
            registered: Date.now(),
            lastFired: 0,
        };

        this.addHandler(handler);
        this._runImmediately(name, callback, opts);

        return handler;
    }

    private _runImmediately(
        name: string,
        callback: (payload?: any) => void,
        opts: IqEventBusBrokerHandlerOptions,
    ) {
        if (!opts.immediate) return;

        const handledEvents: IqEventBusNode[] = this._queue
            .eventNodes()
            .filter(
                (node: IqEventBusNode) =>
                    node.topic === name && node.evt.handled,
            );
        const sortedEvents = sortBy(handledEvents, 'offset').reverse();
        const event = get(sortedEvents, '[0].evt');

        if (isEmpty(event)) return;

        !isEmpty(event.payload) ? callback(event.payload) : callback(null);
    }
}
